import { Link, Stack, Text } from '@sixfold/common-ui';
import { isNil } from '@sixfold/typed-primitives';
import React, { useMemo } from 'react';

import { BarList, ColumnChart } from './charts';
import { Card, EmptyState, LoadingState, TourStatusPill } from './ui';
import { Routes } from '../../routes';
import { EMPTY_STATS, STATS_WINDOW_DAYS, useMobileData, useMobileTrackingShare } from '../hooks';
import { useMobileDeviceBreakdown } from '../signals';
import { Breakdown, SeriesPoint } from '../types';

import styles from './mobile.module.css';

const TOP_ACCOUNTS = 8;

const DeviceBarList: React.FC<{ items: Breakdown[] }> = ({ items }) => {
  const total = items.reduce((acc, { value }) => acc + value, 0);
  return (
    <BarList
      items={items}
      formatCount={({ value }) => value.toLocaleString()}
      formatValue={({ value }) => `${total === 0 ? '0.0' : ((value / total) * 100).toFixed(1)}%`}
    />
  );
};

const DeviceBreakdownCards: React.FC = () => {
  const { appVersions, operatingSystems, available, loading, error, days } = useMobileDeviceBreakdown();
  const subtitle = `Devices running the Visibility Drive app, last ${days} days`;
  const emptyText = loading
    ? 'Loading'
    : error !== undefined || !available
      ? 'Device data is not available right now'
      : 'No devices recorded';
  return (
    <>
      <Card title="App versions" subtitle={subtitle}>
        {appVersions.length === 0 ? (
          <Text prominence="subtle">{emptyText}</Text>
        ) : (
          <DeviceBarList items={appVersions} />
        )}
      </Card>
      <Card title="Device OS" subtitle={subtitle}>
        {operatingSystems.length === 0 ? (
          <Text prominence="subtle">{emptyText}</Text>
        ) : (
          <DeviceBarList items={operatingSystems} />
        )}
      </Card>
    </>
  );
};

const STATUS_LABELS: { key: 'completed' | 'ongoing' | 'failed'; label: string }[] = [
  { key: 'completed', label: 'completed' },
  { key: 'ongoing', label: 'working' },
  { key: 'failed', label: 'failed' },
];

const TrackingShareCard: React.FC<{ carrierId: string | null }> = ({ carrierId }) => {
  const { points, loading, error } = useMobileTrackingShare(carrierId);
  const series = useMemo<SeriesPoint[]>(
    () =>
      points.map(({ date, mobile, tracked }) => ({
        label: date.slice(5),
        value: mobile === 0 ? 0 : Math.round((tracked / mobile) * 100),
        secondary: mobile,
      })),
    [points],
  );
  return (
    <Card
      title="Positions received per day"
      subtitle="Share of mobile transports created that day with mobile positions in Visibility">
      {error !== undefined ? (
        <Text prominence="subtle">Trend is not available right now</Text>
      ) : loading ? (
        <Text prominence="subtle">Loading</Text>
      ) : series.length === 0 ? (
        <Text prominence="subtle">No mobile transports in this period</Text>
      ) : (
        <ColumnChart points={series} formatValue={(point) => `${point.value}% of ${point.secondary ?? 0} transports`} />
      )}
    </Card>
  );
};

export const Insights: React.FC<{ companyId?: string }> = ({ companyId }) => {
  const { statsByCompany, companies, totals, loading } = useMobileData();
  const scoped = companyId === undefined ? totals : (statsByCompany.get(companyId) ?? EMPTY_STATS);
  const showTopAccounts = companyId === undefined;

  const reasons = useMemo<Breakdown[]>(
    () => [
      { label: 'Failed or aborted', value: scoped.failed },
      { label: 'Tour warnings', value: scoped.withWarnings },
      { label: 'No mobile positions received', value: scoped.withoutPositions },
    ],
    [scoped],
  );
  const statuses = useMemo<Breakdown[]>(
    () => STATUS_LABELS.map(({ key, label }) => ({ label, value: scoped[key] })),
    [scoped],
  );
  const accounts = useMemo<Breakdown[]>(
    () =>
      companies
        .map(({ companyId: id, companyName }) => ({
          id,
          label: companyName,
          value: statsByCompany.get(id)?.mobile ?? 0,
        }))
        .filter(({ value }) => value > 0)
        .sort((a, b) => b.value - a.value)
        .slice(0, TOP_ACCOUNTS),
    [companies, statsByCompany],
  );

  if (loading) {
    return <LoadingState label="Loading insights" />;
  }

  if (scoped.mobile === 0) {
    return <EmptyState title="No mobile transports yet" description="Insights appear once mobile tours are tracked." />;
  }

  return (
    <div className={styles.cardGrid}>
      <Card title="Positions received" subtitle="Mobile transports where Sixfold received positions from the app">
        <Stack alignItems="end" gap="small">
          <span className={styles.statValue}>
            {scoped.mobile === 0 ? '-' : `${Math.round((scoped.tracked / scoped.mobile) * 100)}%`}
          </span>
          <Text prominence="subtle">
            {scoped.tracked} of {scoped.mobile} mobile transports
          </Text>
        </Stack>
      </Card>

      <TrackingShareCard carrierId={companyId ?? null} />

      <Card title="Transports needing action" subtitle="Tours that failed, raised warnings or never received positions">
        <BarList items={reasons} showShare={false} />
      </Card>

      <Card title="Tour status" subtitle={`Mobile transports by outcome in the last ${STATS_WINDOW_DAYS} days`}>
        <BarList items={statuses} renderLabel={(item) => <TourStatusPill status={item.label} />} />
      </Card>

      {showTopAccounts && <DeviceBreakdownCards />}

      {showTopAccounts && (
        <Card
          title="Biggest mobile accounts"
          subtitle={`Carriers by mobile transports in the last ${STATS_WINDOW_DAYS} days`}>
          <BarList
            items={accounts}
            showShare={false}
            renderLabel={(item) =>
              isNil(item.id) ? (
                item.label
              ) : (
                <Link href={Routes.MobileCompany.generatePath({ company_id: item.id })}>{item.label}</Link>
              )
            }
          />
        </Card>
      )}
    </div>
  );
};
